import { useCallback, useEffect, useState } from 'react';

import { notification } from '@webclient/components/notification';
import useGetFileContentLazily from '@webclient/components/pages/knowledge-base/hooks/useGetFileContentLazily';
import useGetSourceDetailsByIdLazily from '@webclient/components/pages/knowledge-base/hooks/useGetSourceDetailsByIdLazily';

import { storage } from '@webclient/lib/agent';

import useGetCredentialByKeyLazily from '@webclient/hooks/useGetCredentialByKey';

import { SourceDetailsType } from '@webclient/shared-types/sources';

import { MarkdownDialog } from '@knowz/knowz-ui';
import { useTranslate } from '@tolgee/react';
import { useDialogs } from '@toolpad/core/useDialogs';
export default function useMarkdownEditor() {
  const { t } = useTranslate();
  const dialogs = useDialogs();
  const [content, setContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sourceId, setSourceId] = useState<number | null>(null);
  const [isEditable, setIsEditable] = useState(true);

  const { sourceDetailsData, isSourceDetailsLoading } =
    useGetSourceDetailsByIdLazily({
      id: sourceId,
    });

  const { credentialData, isCredentialDataLoading } =
    useGetCredentialByKeyLazily({
      key: sourceDetailsData?.path ?? '',
    });

  const { fileContent, isFileContentLoading } = useGetFileContentLazily({
    url: credentialData?.data.url ?? null,
  });

  const getContentType = useCallback((fileType: string | undefined) => {
    switch (fileType) {
      case 'json':
        return 'application/json';
      case 'pdf':
        return 'application/pdf';
      case 'md':
        return 'text/markdown';
      case 'txt':
        return 'text/plain';
      default:
        return 'text/plain';
    }
  }, []);

  const handleSaveFile = useCallback(
    async (updatedMarkdown: string, sourceDetails: SourceDetailsType) => {
      try {
        setIsLoading(true);
        const contentType = getContentType(sourceDetails.fileType);
        const size = new Blob([updatedMarkdown]).size;
        const sizeInKB = size / 1024;

        const { data } = await storage.createPolicy({
          mimeType: contentType,
          name: sourceDetails.name ?? '',
          size: sizeInKB,
          context: {
            entityType: 'source',
            entityId: sourceDetails.id,
          },
        });

        const result = await storage.uploadFile({
          url: data.url,
          body: updatedMarkdown,
          contentType: contentType,
        });

        if (result.status === 200) {
          notification.success({
            message: t(
              'response.success.saveFile',
              'You have successfully saved the file.',
            ),
          });
          setContent(updatedMarkdown);
          setIsLoading(false);
          return true;
        }
        return false;
      } catch (error) {
        notification.error({
          message: t(
            'response.errors.fileUploadError',
            'Unable to upload the file. Please try again later or check your file permissions.',
          ),
        });
        console.error('Error while uploading file', error);
        setIsLoading(false);
        return false;
      }
    },
    [getContentType, t],
  );

  const openMarkdownEditor = useCallback(
    async (
      entityId: number | string,
      initialContent?: string,
      isEditable = true,
    ) => {
      try {
        setIsLoading(true);
        setSourceId(Number(entityId));
        setIsEditable(isEditable);

        if (initialContent) {
          setContent(initialContent);

          dialogs.open(MarkdownDialog, {
            title: sourceDetailsData?.name || '',
            initialMarkdown: initialContent,
            onSave: (updatedMarkdown: string) => {
              if (sourceDetailsData) {
                return handleSaveFile(updatedMarkdown, sourceDetailsData);
              }
              return false;
            },
            isEditable,
          });

          setIsLoading(false);
          return;
        }
      } catch (error) {
        console.error('Error opening markdown editor', error);

        notification.error({
          message: t(
            'response.errors.openFile',
            'Unable to open the file. Please try again later or check your file permissions.',
          ),
        });
        setIsLoading(false);
      }
    },
    [dialogs, handleSaveFile, sourceDetailsData, t],
  );

  const handleFileContentLoaded = useCallback(() => {
    if (!fileContent || !sourceDetailsData) return;

    dialogs.open(MarkdownDialog, {
      title: sourceDetailsData.name || '',
      initialMarkdown: fileContent,
      onSave: (updatedMarkdown: string) =>
        handleSaveFile(updatedMarkdown, sourceDetailsData),
      isEditable,
    });

    setContent(fileContent);
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileContent, sourceDetailsData, handleSaveFile]);

  useEffect(() => {
    if (fileContent && !content && sourceDetailsData) {
      handleFileContentLoaded();
    }
  }, [fileContent, content, sourceDetailsData, handleFileContentLoaded]);

  return {
    openMarkdownEditor,
    handleSaveFile,
    isLoading:
      isLoading ||
      isSourceDetailsLoading ||
      isCredentialDataLoading ||
      isFileContentLoading,
    sourceDetailsData,
    content,
  };
}
